import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import MobileLayout from '../MobileLayout';
import { WaveListItem, WaveResponse } from '../../../services/fulfillment/types';
import { WAVE_TYPE_LABELS } from '../../../services/fulfillment/constants';
import { getWave, listWaves } from '../../../services/fulfillment/waveApi';

const PickHome: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [availableWaves, setAvailableWaves] = useState<WaveListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listWaves('in_progress')
      .then(waves => setAvailableWaves(waves))
      .catch(() => setError('Failed to load available waves.'))
      .finally(() => setLoading(false));
  }, []);

  const handleStartPicking = async (waveId: number, waveType: string) => {
    setActionLoading(true);
    setError(null);
    try {
      const wave = (await getWave(waveId)) as WaveResponse;
      const route =
        waveType === 'paa_only'
          ? `/mobile/pick/paa-wave/${waveId}`
          : waveType === 'mixed'
          ? `/mobile/pick/mixed-wave/${waveId}`
          : `/mobile/pick/bike-wave/${waveId}`;
      history.push(route, { wave });
    } catch {
      setError('Failed to load wave.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <MobileLayout>
      <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
        Pick
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : availableWaves.length === 0 ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No active waves
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ask a manager to create a wave from the Fulfillment Orders screen.
            </Typography>
            <Button variant="outlined" fullWidth onClick={() => history.push('/mobile')}>
              Return Home
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {availableWaves.map(wave => (
            <Card key={wave.waveId}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {WAVE_TYPE_LABELS[wave.type]} Wave #{wave.waveId}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {wave.completedOrders} of {wave.totalOrders} orders completed
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleStartPicking(wave.waveId, wave.type)}
                  disabled={actionLoading}
                >
                  {actionLoading ? <CircularProgress size={20} /> : 'Start Picking'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </MobileLayout>
  );
};

export default PickHome;
